import { Formik } from 'formik';
import { useCallback } from 'react';
import { object } from 'yup';

import type { Chamfer, Measurement } from '@frontend/api';
import { RUNES_CALLOUT_TYPES } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  Flex,
  GDTSymbolText,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { MultiplierInput } from './shared/MultiplierInput';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { ToleranceInputGroup } from './shared/ToleranceInputGroup';
import {
  createRequiredStringNumberField,
  createBooleanField,
  createStringToleranceValidationGroup,
  createMultiplierValidationGroup,
  createStringAngleToleranceValidationGroup,
  parsePrecisionValue,
} from './shared/validation';
import {
  buildPrecisionMeasurementObject,
  stringToleranceFormValuesToConfig,
  extractMultiplier,
} from './shared/valueBuilders';

type ChamferFormValues = {
  legLength: string;
  legLengthHasTolerance: boolean;
  legLengthToleranceType: string;
  legLengthToleranceUpper?: string;
  legLengthToleranceLower?: string;
  legLengthToleranceSymmetric?: string;
  hasAngle: boolean;
  angle?: string;
  angleHasTolerance: boolean;
  angleToleranceType: string;
  angleToleranceUpper?: string;
  angleToleranceLower?: string;
  angleToleranceSymmetric?: string;
  hasSecondLegLength: boolean;
  secondLegLength?: string;
  secondLegLengthHasTolerance: boolean;
  secondLegLengthToleranceType: string;
  secondLegLengthToleranceUpper?: string;
  secondLegLengthToleranceLower?: string;
  secondLegLengthToleranceSymmetric?: string;
  hasMult: boolean;
  mult?: number;
};

const initialValues: ChamferFormValues = {
  legLength: '0',
  legLengthHasTolerance: false,
  legLengthToleranceType: 'symmetric',
  legLengthToleranceUpper: undefined,
  legLengthToleranceLower: undefined,
  legLengthToleranceSymmetric: undefined,
  hasAngle: false,
  angle: undefined,
  angleHasTolerance: false,
  angleToleranceType: 'symmetric',
  angleToleranceUpper: undefined,
  angleToleranceLower: undefined,
  angleToleranceSymmetric: undefined,
  hasSecondLegLength: false,
  secondLegLength: undefined,
  secondLegLengthHasTolerance: false,
  secondLegLengthToleranceType: 'symmetric',
  secondLegLengthToleranceUpper: undefined,
  secondLegLengthToleranceLower: undefined,
  secondLegLengthToleranceSymmetric: undefined,
  hasMult: false,
  mult: undefined,
};

const validationSchema = object().shape({
  legLength: createRequiredStringNumberField('Leg length'),
  ...createStringToleranceValidationGroup('legLength'),
  hasAngle: createBooleanField(),
  ...createStringAngleToleranceValidationGroup('angle'),
  hasSecondLegLength: createBooleanField(),
  ...createStringToleranceValidationGroup('secondLegLength'),
  ...createMultiplierValidationGroup(),
});

/**
 * Formats chamfer summary for preview
 */
const formatChamferSummary = (values: ChamferFormValues): string => {
  let summary = '';

  if (values.hasMult && values.mult !== undefined) {
    summary += `${values.mult}×`;
  }

  summary += `${values.legLength}`;

  // Add leg length tolerance if present
  if (values.legLengthHasTolerance) {
    if (
      values.legLengthToleranceType === 'symmetric' &&
      values.legLengthToleranceSymmetric
    ) {
      summary += ` ±${values.legLengthToleranceSymmetric}`;
    } else if (values.legLengthToleranceType === 'asymmetric') {
      if (values.legLengthToleranceUpper && values.legLengthToleranceLower) {
        summary += ` +${values.legLengthToleranceUpper}/-${values.legLengthToleranceLower}`;
      }
    }
  }

  if (values.hasAngle && values.angle) {
    summary += ` × ${values.angle}°`;
  }

  return summary;
};

export const ChamferForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: ChamferFormValues) => {
      // Build leg length measurement
      const parsedLegLength = parsePrecisionValue(values.legLength);
      if (!parsedLegLength) {
        return;
      }

      const legLengthToleranceConfig = stringToleranceFormValuesToConfig(
        {
          hasTolerance: values.legLengthHasTolerance,
          toleranceType: values.legLengthToleranceType as any,
          toleranceSymmetric: values.legLengthToleranceSymmetric,
          toleranceUpper: values.legLengthToleranceUpper,
          toleranceLower: values.legLengthToleranceLower,
        },
        parsedLegLength.numericValue,
      );

      const legLength = buildPrecisionMeasurementObject(
        values.legLength,
        legLengthToleranceConfig,
      );
      if (!legLength) {
        return;
      }

      // Build angle measurement if provided
      let angle: Measurement | undefined;
      if (values.hasAngle && values.angle) {
        const parsedAngle = parsePrecisionValue(values.angle);
        if (parsedAngle) {
          const angleToleranceConfig = stringToleranceFormValuesToConfig(
            {
              hasTolerance: values.angleHasTolerance,
              toleranceType: values.angleToleranceType as any,
              toleranceSymmetric: values.angleToleranceSymmetric,
              toleranceUpper: values.angleToleranceUpper,
              toleranceLower: values.angleToleranceLower,
            },
            parsedAngle.numericValue,
          );

          angle = buildPrecisionMeasurementObject(
            values.angle,
            angleToleranceConfig,
          );
        }
      }

      // Build second leg length measurement if provided
      let secondLegLength: Measurement | undefined;
      if (values.hasSecondLegLength && values.secondLegLength) {
        const parsedSecondLegLength = parsePrecisionValue(
          values.secondLegLength,
        );
        if (parsedSecondLegLength) {
          const secondLegLengthToleranceConfig =
            stringToleranceFormValuesToConfig(
              {
                hasTolerance: values.secondLegLengthHasTolerance,
                toleranceType: values.secondLegLengthToleranceType as any,
                toleranceSymmetric: values.secondLegLengthToleranceSymmetric,
                toleranceUpper: values.secondLegLengthToleranceUpper,
                toleranceLower: values.secondLegLengthToleranceLower,
              },
              parsedSecondLegLength.numericValue,
            );

          secondLegLength = buildPrecisionMeasurementObject(
            values.secondLegLength,
            secondLegLengthToleranceConfig,
          );
        }
      }

      const chamfer: Chamfer = {
        calloutType: RUNES_CALLOUT_TYPES.CHAMFER,
        mult: extractMultiplier({ hasMult: values.hasMult, mult: values.mult }),
        legLength,
        angle: angle ?? null,
        secondLegLength: secondLegLength ?? null,
      };

      onSubmit(chamfer, formatChamferSummary(values));
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => (
        <SubFormWrapper
          isValid={isValid}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          renderSelectedType={renderSelectedType}
          submitLabel="Add Chamfer"
        >
          <FormPreviewSection>
            <GDTSymbolText>{formatChamferSummary(values)}</GDTSymbolText>
          </FormPreviewSection>

          <Flex direction="column" gap={1} px={1} pb={1}>
            {/* Leg Length - Required */}
            <FormControl>
              <FormLabel>Leg Length</FormLabel>
              <NumberValueField
                name="legLength"
                value={values.legLength}
                onChange={(value) => setFieldValue('legLength', value)}
                size="sm"
                min={0}
              />
              {errors.legLength && touched.legLength && (
                <FormErrorMessage>{errors.legLength}</FormErrorMessage>
              )}
            </FormControl>

            {/* Leg Length Tolerance */}
            <ToleranceInputGroup
              hasToleranceFieldName="legLengthHasTolerance"
              hasTolerance={values.legLengthHasTolerance}
              onHasToleranceChange={(checked) =>
                setFieldValue('legLengthHasTolerance', checked)
              }
              toleranceTypeFieldName="legLengthToleranceType"
              toleranceType={values.legLengthToleranceType as any}
              onToleranceTypeChange={(type) =>
                setFieldValue('legLengthToleranceType', type)
              }
              symmetricFieldName="legLengthToleranceSymmetric"
              symmetricValue={values.legLengthToleranceSymmetric}
              onSymmetricChange={(value) =>
                setFieldValue('legLengthToleranceSymmetric', value)
              }
              upperFieldName="legLengthToleranceUpper"
              upperValue={values.legLengthToleranceUpper}
              onUpperChange={(value) =>
                setFieldValue('legLengthToleranceUpper', value)
              }
              lowerFieldName="legLengthToleranceLower"
              lowerValue={values.legLengthToleranceLower}
              onLowerChange={(value) =>
                setFieldValue('legLengthToleranceLower', value)
              }
              errors={errors}
              touched={touched}
              checkboxLabel="Include leg length tolerance"
            />

            {/* Angle - Optional */}
            <Checkbox
              name="hasAngle"
              isChecked={values.hasAngle}
              onChange={(e) => setFieldValue('hasAngle', e.target.checked)}
              size="sm"
            >
              Include angle
            </Checkbox>
            {values.hasAngle && (
              <>
                <FormControl>
                  <FormLabel>Angle</FormLabel>
                  <NumberValueField
                    name="angle"
                    value={values.angle ?? ''}
                    onChange={(value) => setFieldValue('angle', value)}
                    size="sm"
                    min={0}
                    max={180}
                    allowDecimals={true}
                  />
                  {errors.angle && touched.angle && (
                    <FormErrorMessage>{errors.angle}</FormErrorMessage>
                  )}
                </FormControl>

                <ToleranceInputGroup
                  hasToleranceFieldName="angleHasTolerance"
                  hasTolerance={values.angleHasTolerance}
                  onHasToleranceChange={(checked) =>
                    setFieldValue('angleHasTolerance', checked)
                  }
                  toleranceTypeFieldName="angleToleranceType"
                  toleranceType={values.angleToleranceType as any}
                  onToleranceTypeChange={(type) =>
                    setFieldValue('angleToleranceType', type)
                  }
                  symmetricFieldName="angleToleranceSymmetric"
                  symmetricValue={values.angleToleranceSymmetric}
                  onSymmetricChange={(value) =>
                    setFieldValue('angleToleranceSymmetric', value)
                  }
                  upperFieldName="angleToleranceUpper"
                  upperValue={values.angleToleranceUpper}
                  onUpperChange={(value) =>
                    setFieldValue('angleToleranceUpper', value)
                  }
                  lowerFieldName="angleToleranceLower"
                  lowerValue={values.angleToleranceLower}
                  onLowerChange={(value) =>
                    setFieldValue('angleToleranceLower', value)
                  }
                  errors={errors}
                  touched={touched}
                  checkboxLabel="Include angle tolerance"
                />
              </>
            )}

            {/* Second Leg Length - Optional */}
            <Checkbox
              name="hasSecondLegLength"
              isChecked={values.hasSecondLegLength}
              onChange={(e) =>
                setFieldValue('hasSecondLegLength', e.target.checked)
              }
              size="sm"
            >
              Include second leg length
            </Checkbox>
            {values.hasSecondLegLength && (
              <>
                <FormControl>
                  <FormLabel>Second Leg Length</FormLabel>
                  <NumberValueField
                    name="secondLegLength"
                    value={values.secondLegLength ?? ''}
                    onChange={(value) =>
                      setFieldValue('secondLegLength', value)
                    }
                    size="sm"
                    min={0}
                  />
                  {errors.secondLegLength && touched.secondLegLength && (
                    <FormErrorMessage>
                      {errors.secondLegLength}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <ToleranceInputGroup
                  hasToleranceFieldName="secondLegLengthHasTolerance"
                  hasTolerance={values.secondLegLengthHasTolerance}
                  onHasToleranceChange={(checked) =>
                    setFieldValue('secondLegLengthHasTolerance', checked)
                  }
                  toleranceTypeFieldName="secondLegLengthToleranceType"
                  toleranceType={values.secondLegLengthToleranceType as any}
                  onToleranceTypeChange={(type) =>
                    setFieldValue('secondLegLengthToleranceType', type)
                  }
                  symmetricFieldName="secondLegLengthToleranceSymmetric"
                  symmetricValue={values.secondLegLengthToleranceSymmetric}
                  onSymmetricChange={(value) =>
                    setFieldValue('secondLegLengthToleranceSymmetric', value)
                  }
                  upperFieldName="secondLegLengthToleranceUpper"
                  upperValue={values.secondLegLengthToleranceUpper}
                  onUpperChange={(value) =>
                    setFieldValue('secondLegLengthToleranceUpper', value)
                  }
                  lowerFieldName="secondLegLengthToleranceLower"
                  lowerValue={values.secondLegLengthToleranceLower}
                  onLowerChange={(value) =>
                    setFieldValue('secondLegLengthToleranceLower', value)
                  }
                  errors={errors}
                  touched={touched}
                  checkboxLabel="Include second leg length tolerance"
                />
              </>
            )}

            {/* Multiplier */}
            <MultiplierInput
              hasMultFieldName="hasMult"
              hasMult={values.hasMult}
              onHasMultChange={(checked) => setFieldValue('hasMult', checked)}
              multFieldName="mult"
              multValue={values.mult}
              onMultChange={(value) => setFieldValue('mult', value)}
              errors={errors}
              touched={touched}
            />
          </Flex>
        </SubFormWrapper>
      )}
    </Formik>
  );
};
